"use client";

import { useEffect, useMemo, useState } from "react";

import { Button } from "@/shadcn/ui/button";
import { Database } from "@punchup/data/supabase.types";
import { Loader } from "@/components/common/icons";
import PhoneInput from "@/components/common/phone-input";
import { SignInCodeForm } from "@punchup/ui-common/components/signin";
import { Smartphone } from "lucide-react";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import { isValidPhoneNumber } from "libphonenumber-js/max";
import { toast } from "@/shadcn/ui/use-toast";

interface ModalBodyProps {
  setIsOpen: (isOpen: boolean) => void;
}

const ModalBody = ({ setIsOpen }: ModalBodyProps) => {
  const supabase = useMemo(() => {
    return createClientComponentClient<Database>({});
  }, []);

  const [body, setBody] = useState<"PHONE_INPUT" | "CODE_INPUT">("PHONE_INPUT");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const isValid = isValidPhoneNumber(phoneNumber);
    setError(!isValid && phoneNumber.length > 0 ? "Invalid phone number" : "");
  }, [phoneNumber]);

  const onSubmit = async () => {
    setIsLoading(true);

    const phone = phoneNumber.replace(/[^0-9]/g, "");
    const { data } = await supabase.auth.getUser();

    if (!phone) {
      setError("Invalid phone number");
      setIsLoading(false);
      return;
    }

    if (data?.user?.phone === phone) {
      setError("This phone number is already associated with your account.");
      setIsLoading(false);
      return;
    }

    if (!isNewUser) {
      setError("This phone number is already associated with another account.");
      setIsLoading(false);
      return;
    }

    const { error: updateError } = await supabase.auth.updateUser({
      phone: phoneNumber,
    });

    if (updateError) {
      setError(updateError.message);
      setIsLoading(false);
      return;
    }

    setBody("CODE_INPUT");

    setIsLoading(false);
  };

  const onPhoneStatusChange = (
    isNewUser: boolean,
    showTermsAccept: boolean
  ) => {
    setIsNewUser(isNewUser);
  };

  const onCodeSuccess = () => {
    setIsOpen(false);
    toast({
      title: "Phone number added",
      description: "You can now receive SMS notifications",
    });
  };

  const resetForm = () => {
    setPhoneNumber("");
    setError("");
    setBody("PHONE_INPUT");
  };

  if (body === "CODE_INPUT") {
    return (
      <SignInCodeForm
        email={""}
        phone={phoneNumber}
        signInMethod={"phone"}
        resetForm={resetForm}
        postalCodeRequired={false}
        enteredPostalCode={""}
        enteredCountryCode={null}
        callback={onCodeSuccess}
        isSignIn={false}
      />
    );
  }

  return (
    <div className="flex flex-col items-stretch gap-6 mt-4 md:mt-0 px-4 md:px-0 pb-4 md:pb-0">
      <div className="flex flex-row justify-center w-full">
        <div className="p-4 bg-gray-100 rounded-full">
          <Smartphone width={40} height={40} />
        </div>
      </div>

      <div className="flex flex-col items-center justify-center gap-3">
        <h1 className="font-sans text-[17px] font-bold leading-tight text-center normal-case md:text-xl">
          Hey! Punchup now supports SMS.
        </h1>
        <p className="text-secondary-blue text-[13px] md:text-[16px] text-center leading-tight">
          Add your phone number and we&apos;ll text you updates on specials,
          tour dates and more!
        </p>
      </div>

      <div className="flex flex-col w-full">
        <p className="font-semibold text-start text-sm mb-2">Phone number</p>

        <PhoneInput
          phoneNumber={phoneNumber}
          setPhoneNumber={setPhoneNumber}
          onPhoneStatusChange={onPhoneStatusChange}
        />

        {error && <p className="text-xs text-red-500">{error}</p>}

        <br />

        <div className="flex justify-center">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <div className="relative flex items-center">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="peer sr-only"
                aria-label="Accept SMS notifications"
              />
              <div
                className={`
                    w-4 h-4 border rounded
                    transition-colors duration-200 ease-in-out
                    ${termsAccepted ? "bg-blue-600 border-blue-600" : "bg-white border-gray-300"}
                    peer-focus:ring-2 peer-focus:ring-blue-500 peer-focus:ring-offset-2
                  `}
              >
                {termsAccepted && (
                  <svg
                    className="w-4 h-4 text-white"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M13.333 4L6 11.333L2.667 8"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </div>
            </div>
            <span className="text-sm text-gray-700">
              I agree to receive SMS notifications from Punchup.
            </span>
          </label>
        </div>
      </div>

      <div className="flex flex-col gap-4 border-t border-border dark:bg-background/20">
        <Button
          variant="default"
          className="bg-primary font-medium text-[14px] py-2 w-full rounded-md transition-colors ease-in-out duration-200  hover:bg-[#ff6e3c]"
          disabled={!phoneNumber || !!error || isLoading || !termsAccepted}
          onClick={() => onSubmit()}
        >
          {isLoading ? <Loader /> : "Add phone number"}
        </Button>

        <Button
          className="w-full"
          variant="secondary"
          onClick={() => {
            setPhoneNumber("");
            setError("");
            setBody("PHONE_INPUT");
            setIsOpen(false);
          }}
        >
          Skip for now
        </Button>
      </div>
    </div>
  );
};

export default ModalBody;
